import { ShoppingCart, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getOptimizedImageUrl } from "@/lib/imageHandler";
import { ProductInfoRow } from "./ProductInfoRow";
import { FavoriteHeartButton } from "@/components/favorites/FavoriteHeartButton";

export interface StockStatus {
  text: string;
  type: "in-stock" | "out-of-stock" | "backorder" | "discontinued";
}

export interface PartsListItemProps {
  id: string;
  name: string;
  number: string;
  defaultImagePath?: string;
  imageUrl?: string;
  price: number;
  priceFormatted?: string;
  stockStatus: StockStatus;
  quantity: number | string;
  stockLevel?: number;
  neverOutOfStock?: boolean;
  onQuantityChange: (value: string) => void;
  onQuantityBlur?: () => void;
  onAddToCart: (e: React.MouseEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  index: number;
  /** Extra badges to render after the name (e.g. VIN compatibility) */
  nameSuffix?: React.ReactNode;
  /** Render content below the main row (e.g. replacement options) */
  expandedContent?: React.ReactNode;
  /** If true, hides price + add-to-cart and shows custom trailing content */
  isDiscontinued?: boolean;
  /** If true, hides quantity input + "Add to Cart" button (capability gating) */
  hideCart?: boolean;
  /** Custom trailing content for discontinued items */
  trailingContent?: React.ReactNode;
  /** Vendor part number (unmapped field) */
  vendorPartNumber?: string;
  /** Unit of measure (unmapped field) */
  unitOfMeasure?: string;
  /** Whether the UOM value comes from real API data (not mock) */
  unitOfMeasureIsMapped?: boolean;
  /** Whether to show unmapped fields (driven by renderMockMappings flag) */
  showUnmappedFields?: boolean;
  /** Callback for "Add to Favorites" heart button (legacy) */
  onAddToFavorites?: (e: React.MouseEvent) => void;
  /** Product ID for context-driven favorites (preferred over onAddToFavorites) */
  favoriteProductId?: string;
  /** Product type – "bom" renders a Kit badge */
  productType?: string;
  /** Show spinner & disable Add button while a request is pending */
  isAdding?: boolean;
}

export function PartsListItem({
  id,
  name,
  number,
  defaultImagePath,
  imageUrl,
  price,
  priceFormatted,
  stockStatus,
  quantity,
  stockLevel,
  neverOutOfStock,
  onQuantityChange,
  onQuantityBlur,
  onAddToCart,
  onClick,
  index,
  nameSuffix,
  expandedContent,
  isDiscontinued,
  hideCart,
  trailingContent,
  vendorPartNumber,
  unitOfMeasure,
  unitOfMeasureIsMapped,
  showUnmappedFields,
  onAddToFavorites,
  favoriteProductId,
  productType,
  isAdding,
}: PartsListItemProps) {
  const outOfStock = !neverOutOfStock && (stockLevel !== undefined && stockLevel <= 0);
  const maxStock = (!neverOutOfStock && stockLevel !== undefined && stockLevel > 0) ? stockLevel : undefined;
  const numQty = typeof quantity === 'string' ? parseInt(quantity, 10) : quantity;
  const isAddDisabled = outOfStock || isNaN(numQty) || numQty <= 0 || !!isAdding;

  // Use getOptimizedImageUrl if we have a raw path, otherwise fall back to imageUrl
  const imgSrc = defaultImagePath
    ? getOptimizedImageUrl(defaultImagePath, { width: 48, height: 48, format: 'webp', crop: 5 })
    : imageUrl || '';

  return (
    <div
      className={cn(
        "border-b border-border hover:bg-accent/40 transition-colors py-2.5 px-2",
        onClick && "cursor-pointer group",
        index % 2 === 1 && "bg-muted/40"
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={name}
              className="w-12 h-12 object-cover rounded"
            />
          ) : (
            <div className="w-12 h-12 flex items-center justify-center bg-muted/50 border border-border rounded">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>

        <ProductInfoRow
          name={name}
          number={number}
          price={price}
          priceFormatted={priceFormatted}
          stockStatus={stockStatus}
          isDiscontinued={isDiscontinued}
          productType={productType}
          nameSuffix={nameSuffix}
          onClick={onClick}
          vendorPartNumber={vendorPartNumber}
          unitOfMeasure={unitOfMeasure}
          unitOfMeasureIsMapped={unitOfMeasureIsMapped}
          showUnmappedFields={showUnmappedFields}
        />

        {!isDiscontinued && !hideCart ? (
          <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Input
              id={`qty-${id}`}
              type="number"
              min={0}
              max={maxStock}
              step={1}
              value={quantity === 0 ? '' : quantity}
              onChange={(e) => onQuantityChange(e.target.value)}
              onBlur={onQuantityBlur}
              className="w-14 h-9 text-center text-sm"
              onClick={(e) => e.stopPropagation()}
              disabled={outOfStock}
            />
            <span className={cn(isAddDisabled && "cursor-not-allowed")}>
              <Button
                size="sm"
                onClick={onAddToCart}
                disabled={isAddDisabled}
              >
                {isAdding ? (
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                ) : (
                  <ShoppingCart className="h-3 w-3 mr-1.5" />
                )}
                Add
              </Button>
            </span>
            {favoriteProductId ? (
              <span onClick={(e) => e.stopPropagation()}>
                <FavoriteHeartButton productId={favoriteProductId} size="sm" />
              </span>
            ) : onAddToFavorites ? (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onAddToFavorites(e); }}
              >
                <Package className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        ) : (
          trailingContent && (
            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {trailingContent}
            </div>
          )
        )}
      </div>

      {expandedContent && (
        <div onClick={(e) => e.stopPropagation()}>
          {expandedContent}
        </div>
      )}
    </div>
  );
}

export function getSimpleStockStatus(stockLevel: number): StockStatus {
  return stockLevel > 0
    ? { text: "In Stock", type: "in-stock" }
    : { text: "Out of Stock", type: "out-of-stock" };
}
